import React, {
    useCallback,
    useState,
} from 'react'
import { noop } from 'lodash'
import {
    Button,
    TextField,
} from '@mui/material'
import { SerializedStyles } from '@emotion/react'

import { useTranslation } from 'shared/hooks/useSafeTranslation'
import useStyle from 'shared/hooks/useStyle'
import { textFieldStyle } from 'shared/TextStepField/TextStepField.style'

import styles from './ManualInput.style'

type Props = {
    onChange?: (args: { value: string | null; close?: boolean }) => void;
    onSubmit: (value: string) => void;
    onCancel?: () => void;
    showCancel?: boolean;
    showSubmit?: boolean;
    validationRegEx?: string;
    validationErrorMessage?: string;
    customClasses?: {
        contextContainer?: SerializedStyles,
        button?: SerializedStyles,
    },
}

const ManualInput = ({
    onChange = noop,
    onSubmit,
    onCancel,
    showCancel = true,
    showSubmit = false,
    validationRegEx,
    validationErrorMessage,
    customClasses,
}: Readonly<Props>) => {
    const classes = useStyle(styles)
    const textFieldStyles = useStyle(textFieldStyle)
    const { t } = useTranslation()

    const [
        value,
        setValue,
    ] = useState('')

    const [
        error,
        setError,
    ] = useState(false)

    const validate = useCallback((v) => {
        const inputRegexp = new RegExp(validationRegEx)

        if (v.match(inputRegexp)) {
            return true
        }
        return false
    }, [validationRegEx])

    const onInputChange = useCallback((event) => {
        const newInputValue = event.target.value

        if (validationRegEx && !validate(newInputValue)) {
            setError(true)
            onChange({ value: null })
        } else {
            setError(false)
            onChange({ value: newInputValue })
        }
        setValue(newInputValue)
    }, [
        onChange,
        validate,
        validationRegEx,
    ])

    const handleCancel = useCallback(() => {
        onChange(
            {
                value: null,
                close: true,
            },
        )
        if (onCancel) {
            onCancel()
        }
    }, [
        onChange,
        onCancel,
    ])

    const handleSubmit = useCallback(() => {
        onSubmit(value)
    }, [
        value,
        onSubmit,
    ])

    const errorText = error ? validationErrorMessage : null

    return (
        <>
            <div css={[
                classes.contextContainer,
                customClasses?.contextContainer,
            ]}
            >
                <TextField
                    id="outlined-helperText"
                    value={value}
                    fullWidth
                    size="small"
                    sx={textFieldStyles}
                    variant="outlined"
                    onChange={onInputChange}
                    error={Boolean(error)}
                    helperText={errorText}
                />
            </div>
            <div css={classes.actions}>
                {
                    showSubmit && (
                        <Button
                            css={[
                                classes.button,
                                customClasses?.button,
                            ]}
                            data-testid="ok-button"
                            onClick={handleSubmit}
                            disabled={value === '' || error}
                        >
                            {t('OK')}
                        </Button>
                    )
                }
                { showCancel && (
                    <Button
                        data-testid="cancel-button"
                        css={[
                            classes.button,
                            customClasses?.button,
                        ]}
                        onClick={handleCancel}
                    >
                        {t('CANCEL')}
                    </Button>
                )}
            </div>

        </>
    )
}

export default ManualInput
